import { ChangeDetectionStrategy, Component, NgZone, OnInit, signal } from '@angular/core';
import { NavigationEnd, NavigationStart } from '@angular/router';
import { AnalyticsApi } from 'api/analytics/analytics-api.js';
import { EmptyApiObject } from 'api/api-models.js';
import { AppSetupApi } from 'api/app-setup/app-setup-api.js';
import { consoleHelper } from 'lib/helpers/console-helper.class.js';
import { reloadHelper } from 'lib/helpers/reload-helper.class.js';
import { timeHelper } from 'lib/helpers/time-helper.class.js';
import { AppStableService } from 'lib/services/app-stable.service.js';
import { DialogType } from 'lib/ui/dialog/index.js';
import { catchError, interval, map, of, switchMap } from 'rxjs';
import { CoreAppComponent } from 'src/app/core/core-app.component.js';
import { environment } from './environments/environment.js';
import { IAppVersionState } from './services/app-version.service.js';

@Component({
	selector: 'app-root-component',
	templateUrl: './root.component.html',
	styleUrls: ['./root.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class RootComponent extends CoreAppComponent implements OnInit {
	public readonly dialogTypes: DialogType[] = this.dependencies.dialogService.getAllDialogTypes();
	public readonly initialAppVersionState = signal<IAppVersionState | undefined>(undefined);
	public readonly showAppVersionLoadingError = signal<boolean>(false);
	private readonly checkAppVersionIntervalSeconds: number = 60 * 10; // every 10 min
	private readonly checkFeedsIntervalSeconds: number = 60 * 5; // every 5 min

	constructor(private readonly appStableService: AppStableService, private readonly ngZone: NgZone) {
		super();

		this.dependencies.themeService.initTheme();
		this.setHtmlClasses();
	}

	ngOnInit(): void {
		this.checkAppStableState();

		// subscribe to app setup right away (do not wait for app to become stable)
		// this is important for SSR and hydration
		this.subscribeToInitAppSetup();

		super.subscribeToObservable(
			this.appStableService.appIsStable_.pipe(
				map((isStable) => {
					if (isStable) {
						// app is stable, register any recurring / interval subscription here
						super.subscribeToObservable(
							this.dependencies.router.events.pipe(
								map((data) => {
									// close on both start & end
									if (data instanceof NavigationEnd || data instanceof NavigationStart) {
										super.closeAllMenus();
										this.dependencies.tooltipService.hideAllTooltips();
									}
								})
							)
						);

						// init app context check
						this.subscribeToAppContextCheck();

						// init feeds
						this.subscribeToFeeds();

						// log page init
						this.logPageInitEvent();

						// When app is stable disable custom transfer state implementation to ensure
						// we get fresh data on subsequent requests
						if (this.isBrowserPlatform()) {
							this.dependencies.httpService.disableTransferState();
						}
					}
				})
			)
		);
	}

	handleUpdateApp(): void {
		if (!this.isBrowserPlatform()) {
			return;
		}
		reloadHelper.handleUpdateApp();
	}

	private checkAppStableState(): void {
		// do not run outside of browser platform
		if (!this.isBrowserPlatform()) {
			return;
		}
		this.ngZone.runOutsideAngular(() => {
			setTimeout(() => {
				if (!this.appStableService.appIsStable_.value) {
					const msg = `App is not stable!`;
					console.warn(msg);

					if (!environment.production) {
						alert(msg);
					}
				}
			}, 4000);
		});
	}

	private setHtmlClasses(): void {
		/**
		 * This is also set by server.ts in production
		 */
		if (this.isBrowserPlatform()) {
			const htmlElem = document.getElementsByTagName('html')?.[0];

			if (htmlElem) {
				const screen = this.screenService.currentScreen();
				htmlElem.dataset['platform'] = screen.platform;
			}
		}
	}

	private logPageInitEvent(): void {
		if (!this.isBrowserPlatform()) {
			// only log in browser
			return;
		}

		super.subscribeToObservable(
			this.httpService
				.post<EmptyApiObject, AnalyticsApi.ILogPageInitRequest>((builder) => {
					return builder
						.withCustomApiRoute('analytics', 'logPageInit')
						.withData({
							timezone: this.dependencies.timeService.currentZone.name,
							url: window.location.href,
							referrer: document.referrer
						})
						.build();
				})
				.pipe(map((response) => {}))
		);
	}

	private subscribeToInitAppSetup(): void {
		super.subscribeToObservable(
			this.dependencies.coreAppService.initAppContext().pipe(
				map((appContext) => {
					this.initialAppVersionState.set(
						this.dependencies.appVersionService.getAppVersionState(appContext.appSetup)
					);
				}),
				catchError((error) => {
					consoleHelper.logToConsole({
						type: 'Error',
						message: 'Could not init app setup',
						data: error
					});

					this.showAppVersionLoadingError.set(true);

					return of(undefined);
				})
			)
		);
	}

	/**
	 * Periodically check and update app setup
	 * This is also used to notify users that new app version is available
	 */
	private subscribeToAppContextCheck(): void {
		if (!this.isBrowserPlatform()) {
			// only subscribe in browser, not on server
			return;
		}
		super.subscribeToObservable(
			interval(timeHelper.getMsFromSeconds(this.checkAppVersionIntervalSeconds)).pipe(
				switchMap(() => {
					return this.dependencies.httpService
						.get<AppSetupApi.IAppContext>((builder) =>
							builder.withCustomApiRoute('appSetup', 'getAppContext').build()
						)
						.pipe(
							map((response) => {
								this.dependencies.appDataService.appContext.set(response.data);

								// update auth token of user
								// this might be useful if something changed in the user data on server without notifying client
								// The data stored in cookie could therefore be out of date, this ensures that auth token
								// is being refreshed periodically
								if (response.data.authToken) {
									this.dependencies.authService.updateAuthUser(response.data.authToken);
								}
							})
						);
				}),
				catchError((error) => {
					consoleHelper.logToConsole({
						type: 'Error',
						message: 'Could not load app version',
						data: error
					});

					return of(undefined);
				})
			)
		);
	}

	/**
	 * Periodically check for feeds
	 */
	private subscribeToFeeds(): void {
		if (!this.isBrowserPlatform()) {
			// only subscribe in browser, not on server
			return;
		}

		super.subscribeToObservable(
			interval(timeHelper.getMsFromSeconds(this.checkFeedsIntervalSeconds)).pipe(
				switchMap(() => {
					// only load feeds if user is authenticated
					if (this.authUser()) {
						return this.dependencies.layoutService.getLoadFeedsObservable();
					}
					return of(undefined);
				}),
				catchError((error) => {
					consoleHelper.logToConsole({
						type: 'Error',
						message: 'Could not load feeds',
						data: error
					});

					return of(undefined);
				})
			)
		);
	}
}
